import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import {
  createDossierHearing,
  deleteDossierHearing,
  listAttendeesForHearings,
  listDossierHearings,
  listHearingAttendees,
  updateDossierHearing,
  type HearingWrite,
} from '../../services/dossiers/hearingService';
import { dossiersKeys } from './useDossiers';

export const hearingAttendeesKey = (hearingId: string) => ['hearing-attendees', hearingId] as const;

function invalidateHearings(qc: ReturnType<typeof useQueryClient>, dossierId: string) {
  qc.invalidateQueries({ queryKey: dossiersKeys.hearings(dossierId) });
  qc.invalidateQueries({ queryKey: dossiersKeys.detail(dossierId) });
  qc.invalidateQueries({ queryKey: dossiersKeys.all });
  qc.invalidateQueries({ queryKey: ['hearing-attendees'] });
}

export function useDossierHearings(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: dossiersKeys.hearings(dossierId ?? ''),
    queryFn: () => listDossierHearings(dossierId!),
    enabled: !!dossierId,
  });
}

export function useHearingsAttendees(hearingIds: string[]) {
  const ids = [...hearingIds].filter(Boolean).sort();
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['hearing-attendees-bulk', ids],
    queryFn: () => listAttendeesForHearings(ids),
    enabled: ids.length > 0,
  });
}

export function useHearingAttendees(hearingId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: hearingAttendeesKey(hearingId ?? ''),
    queryFn: () => listHearingAttendees(hearingId!),
    enabled: !!hearingId,
  });
}

export function useCreateDossierHearing(dossierId: string) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.collaborator);
  return useMutation({
    mutationFn: (input: HearingWrite) =>
      createDossierHearing({
        ...input,
        dossier_id: dossierId,
        created_by: me?.id ?? null,
      }),
    onSuccess: () => invalidateHearings(qc, dossierId),
  });
}

export function useUpdateDossierHearing(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: HearingWrite }) =>
      updateDossierHearing(id, patch),
    onSuccess: () => invalidateHearings(qc, dossierId),
  });
}

export function useDeleteDossierHearing(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDossierHearing(id),
    onSuccess: () => invalidateHearings(qc, dossierId),
  });
}
